import { useParams, Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500">影片不存在或已下架</p>
          <Link
            to="/"
            className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 cursor-pointer transition-colors"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: movie.category, href: `/category/${movie.categorySlug}` },
    { label: movie.title },
  ];

  const pageTitle = `${movie.title} - 麻花影院免费观看 | ${movie.title}在线播放`;
  const pageDesc = `${movie.title}（${movie.englishTitle}）${movie.year}年${movie.region}${movie.category}，导演${movie.director}，主演${movie.actors.join('、')}。${movie.description.substring(0, 100)}...在麻花影院免费在线观看高清全集。`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: pageTitle,
    description: pageDesc,
    author: { '@type': 'Organization', name: '麻花影院' },
    publisher: { '@type': 'Organization', name: '麻花影院' },
    datePublished: `${movie.year}-01-01`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://mahua.tv/detail/${movie.id}` },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <main className="min-h-screen pt-20 pb-12">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <article className="flex flex-col lg:flex-row gap-8 mb-12">
              <div className="flex-shrink-0 w-full lg:w-72">
                <div className="aspect-[2/3] rounded-xl overflow-hidden">
                  <img
                    src={movie.coverUrl}
                    alt={movie.title}
                    title={`${movie.title} - 麻花影院免费观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{movie.englishTitle}</p>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-sm font-bold">
                    {movie.rating} 分
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-700 text-sm">
                    {movie.year}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-700 text-sm">
                    {movie.region}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-700 text-sm">
                    {movie.category}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-700 text-sm">
                    {movie.duration}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-5 rounded-xl bg-background-100">
                  {movie.director && (
                    <div>
                      <span className="text-xs text-foreground-500">导演</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.director}</p>
                    </div>
                  )}
                  {movie.actors.length > 0 && (
                    <div>
                      <span className="text-xs text-foreground-500">主演</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">
                        {movie.actors.join(' / ')}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-foreground-500">状态</span>
                    <p className="text-sm font-medium text-primary-600 mt-0.5">{movie.updateInfo}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">集数</span>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">
                      {movie.episodeTotal === 1 ? '单片' : `${movie.episodeCurrent}/${movie.episodeTotal}集`}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mb-6">
                  <h2 className="font-heading font-semibold text-foreground-950 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>

                <Link
                  to={`/category/${movie.categorySlug}`}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 cursor-pointer transition-all duration-200 whitespace-nowrap text-sm"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  立即观看
                </Link>
              </div>
            </article>

            {relatedMovies.length > 0 && (
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}

            <section className="mt-12 p-6 rounded-xl bg-background-100">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                常见问题
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-1">
                    {movie.title}在哪里可以免费观看？
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    您可以在麻花影院免费观看{movie.title}全集，无需付费或注册会员，直接在线播放，高清画质流畅不卡顿。
                  </p>
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-1">
                    麻花影院{movie.category}资源更新快吗？
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    麻花影院每日更新{movie.category}资源，紧跟最新热播内容，确保您能在第一时间观看到最新的{movie.title}等热门{movie.category}。
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `${movie.title}在哪里可以免费观看？`,
            acceptedAnswer: { '@type': 'Answer', text: `您可以在麻花影院免费观看${movie.title}全集，无需付费或注册会员，直接在线播放，高清画质流畅不卡顿。` },
          },
          {
            '@type': 'Question',
            name: `麻花影院${movie.category}资源更新快吗？`,
            acceptedAnswer: { '@type': 'Answer', text: `麻花影院每日更新${movie.category}资源，紧跟最新热播内容，确保您能在第一时间观看到最新的${movie.title}等热门${movie.category}。` },
          },
        ],
      })}</script>
    </>
  );
}